import { AppealGenerateDetails } from "../../data-model/appealGenerateDetails";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** POST body for backend `pharmacy/appeal/generate`. */
export interface GenerateAppealPayload {
  identifier: string;
  mrn: string;
  drug_description?: string | null;
  plan_name?: string | null;
}

/** Parsed response from backend `pharmacy/appeal/generate`. */
export interface GenerateAppealResponse {
  message?: string;
  success?: boolean;
  details?: AppealGenerateDetails;
}

/**
 * Generates an appeal assessment and letter for a denied pharmacy case.
 *
 * @param payload - Order identifier, MRN, and optional drug/plan overrides
 * @returns Parsed JSON response from backend service
 * @throws {Error} When auth is missing or the API returns a non-OK status
 */
export const generateAppeal = async (
  payload: GenerateAppealPayload,
): Promise<GenerateAppealResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.GENERATE_APPEAL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({
        identifier: payload.identifier,
        mrn: payload.mrn,
        drug_description: payload.drug_description ?? undefined,
        plan_name: payload.plan_name ?? undefined,
      }),
    });

    const responseText = await response.text();
    let parsed: GenerateAppealResponse = {};

    if (responseText.trim()) {
      try {
        parsed = JSON.parse(responseText) as GenerateAppealResponse;
      } catch {
        parsed = { message: responseText };
      }
    }

    if (!response.ok) {
      throw new Error(
        parsed.message ||
          `Failed to generate appeal: ${response.status} - ${responseText}`,
      );
    }

    return parsed;
  } catch (error) {
    logError(error as Error, "generateAppeal");
    throw error;
  }
};
